import { theme, type ThemeConfig } from 'antd'

export interface ThemeOptions {
  colorPrimary?: string
  colorInfo?: string
  colorSuccess?: string
  colorWarning?: string
  colorError?: string
  fontFamily?: string
}

export function getDarkThemeConfig(options: ThemeOptions = {}): ThemeConfig {
  const primary = options.colorPrimary || '#ff3e4d'
  const info = options.colorInfo || '#00e1ff'
  const success = options.colorSuccess || '#2ecc71'
  const warning = options.colorWarning || '#ffb627'
  const error = options.colorError || '#ff3e4d'
  const font =
    options.fontFamily ||
    "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

  return {
    algorithm: theme.darkAlgorithm,
    token: {
      colorPrimary: primary,
      colorInfo: info,
      colorSuccess: success,
      colorWarning: warning,
      colorError: error,
      colorText: '#f6f6f9',
      colorTextSecondary: '#9fa2b8',
      colorTextTertiary: '#6b6f85',
      colorBgBase: '#07070a',
      colorBgLayout: '#07070a',
      colorBgContainer: '#14141d',
      colorBgElevated: '#1a1a28',
      colorBgSpotlight: '#242436',
      colorBorder: 'rgba(255, 255, 255, 0.1)',
      colorBorderSecondary: 'rgba(255, 255, 255, 0.06)',
      borderRadius: 10,
      borderRadiusLG: 14,
      borderRadiusSM: 6,
      fontFamily: font,
      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: '#07070a',
        siderBg: '#0e0e14',
        bodyBg: '#07070a',
      },
      Menu: {
        itemSelectedBg: 'rgba(255, 62, 77, 0.14)',
        itemSelectedColor: primary,
        itemHoverBg: 'rgba(255, 255, 255, 0.04)',
        itemHoverColor: '#f6f6f9',
        itemColor: '#9fa2b8',
        itemActiveBg: 'rgba(255, 62, 77, 0.08)',
        itemBorderRadius: 8,
        subMenuItemBg: 'transparent',
      },
      Card: {
        borderRadiusLG: 14,
        colorBgContainer: '#14141d',
        colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
        headerBg: 'transparent',
      },
      Table: {
        headerBg: 'rgba(20, 20, 29, 0.95)',
        headerColor: '#f6f6f9',
        rowHoverBg: 'rgba(255, 62, 77, 0.05)',
        colorBgContainer: 'transparent',
        borderColor: 'rgba(255, 255, 255, 0.08)',
      },
      Button: {
        primaryShadow: '0 4px 14px rgba(255, 62, 77, 0.35)',
        borderRadius: 8,
      },
      Modal: {
        colorBgElevated: '#14141d',
        headerBg: '#14141d',
        contentBg: '#14141d',
        footerBg: '#14141d',
      },
      Drawer: {
        colorBgElevated: '#14141d',
      },
      Descriptions: {
        colorText: '#f6f6f9',
        colorTextSecondary: '#9fa2b8',
        colorSplit: 'rgba(255, 255, 255, 0.08)',
      },
      Tag: {
        defaultBg: 'rgba(255, 255, 255, 0.06)',
        defaultColor: '#f6f6f9',
        borderRadiusSM: 6,
      },
      Input: {
        colorBgContainer: '#0e0e14',
        colorBorder: 'rgba(255, 255, 255, 0.12)',
        hoverBorderColor: primary,
        activeBorderColor: primary,
      },
      Select: {
        colorBgContainer: '#0e0e14',
        colorBorder: 'rgba(255, 255, 255, 0.12)',
        colorBgElevated: '#1a1a28',
        optionSelectedBg: 'rgba(255, 62, 77, 0.16)',
      },
      Tabs: {
        itemColor: '#9fa2b8',
        itemSelectedColor: primary,
        itemHoverColor: '#f6f6f9',
        inkBarColor: primary,
      },
    },
  }
}

export const darkThemeConfig: ThemeConfig = getDarkThemeConfig()
